import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { AUTOFILL_PATTERN } from 'pxc-editor-core/constants';

import { colors } from '@/globalStyles';
import { COPY_MODE, AUTOFILL_MODE, AUTOFILL_SETTING_LOOP, AUTOFILL_SETTING_REPEAT } from '@/constants';
import InlineButtonsGroup from '@/components/InlineButtonsGroup';
import { AutofillNumericInput, NoneNegativeNumericInput } from '@/components/NumericInput';
import IconAdvancedSettings from '@/components/Icon/CmmeIcons/IconAdvancedSettings';

import { GridContainer, Label } from '@/components/SideBar/styles';

import IconCmmeCheckbox from '../../Icon/CmmeIcons/IconCmmeCheckbox';
import IconCmmeCheckboxSelected from '../../Icon/CmmeIcons/IconCmmeCheckboxSelected';

import IconMinus from '@/icons/icon-cmme-minus-grey.svg';
import IconPlus from '@/icons/icon-cmme-plus-grey.svg';
import IconRadioButton from '@/icons/icon-cmme-radio-button.svg';
import IconRadioButtonSelected from '@/icons/icon-cmme-radio-button-selected.svg';

import { ToggleButton } from '../styles';
import { NextModeWrapper, NextModeItem, Input, PanelHeaderWrapper, PanelHeader, FormWrapper, InputContainerWhite, Icon } from './styles';

const TakeOtherContent = (props) => {
  const { isMatrix, disabled, includeOtherContent, setOtherContentState } = props;

  const handleClick = () => {
    if (!disabled) {
      setOtherContentState(!includeOtherContent)
    }
  }

  return isMatrix ? null : (
    <PanelHeaderWrapper onClick={handleClick}>
      {includeOtherContent ? <IconCmmeCheckboxSelected fill={colors.primary} /> : <IconCmmeCheckbox />}
      <PanelHeader>
        <FormattedMessage id="app.editor.sideMenu.panelLabel.takeOtherContent" defaultMessage="Take other content as well?" />
      </PanelHeader>
    </PanelHeaderWrapper>
  );
};

const AutofillAndCopyForm = props => {
  const {
    start,
    end,
    steps,
    prefix,
    suffix,
    mode,
    pattern,
    isSettingsVisible,
    setting,
    loopsAmount,
    repeatsAmount,
    copiesNo,
    isPatternSelectAvailable,
    compRef,
    onChange,
    includeOtherContent,
    setOtherContentState,
    isNextCellPatternDisabled,
    disabled,
    setAutoFillAndCopyPattern,
    isMatrix,
  } = props;
  const autofillStart = parseInt(start, 10);
  const autofillEnd = parseInt(end, 10);

  const [startError, setStartError] = useState('');
  const [endError, setEndError] = useState('');
  const [stepsError, setStepsError] = useState('');
  const [settingError, setSettingError] = useState('');
  const [copiesNumberError, setCopiesNumberError] = useState('');

  const isAutofillValid = () => mode !== AUTOFILL_MODE || (startError === '' && endError === '' && stepsError === '' && settingError === '');
  const isCopyValid = () => mode !== COPY_MODE || copiesNumberError === '';

  compRef.current = {
    validate: () => isAutofillValid() && isCopyValid(),
  };

  const modesList = [
    {
      icon: <FormattedMessage id="app.editor.menu.autofill" defaultMessage="AutoFill" />,
      selected: mode === AUTOFILL_MODE,
      onClick: disabled ? undefined : () => onChange({ mode: AUTOFILL_MODE }),
    },
    {
      icon: <FormattedMessage id="app.editor.menu.autofillCopy" defaultMessage="Copy" />,
      selected: mode === COPY_MODE,
      onClick: disabled ? undefined : () => onChange({ mode: COPY_MODE }),
    },
  ];
  const isTextFlowAvailable = isMatrix ? false : isPatternSelectAvailable && !isNextCellPatternDisabled;

  const validateFields = () => {
    if (mode === AUTOFILL_MODE) {
      setStartError('');
      setEndError('');
      setStepsError('');
      setSettingError('');

      if (autofillStart === '' || isNaN(autofillStart)) {
        setStartError('Field is not nullable');
      }
      if (autofillEnd === '' || isNaN(autofillEnd)) {
        setEndError('Field is not nullable');
      }
      if (steps <= 0) {
        setStepsError('Must be greater than 0');
      }
      if (steps === '' || isNaN(steps)) {
        setStepsError('Field is not nullable');
      }
      if (isSettingsVisible && ((setting === AUTOFILL_SETTING_LOOP && loopsAmount < 1) || (setting === AUTOFILL_SETTING_REPEAT && repeatsAmount < 1))) {
        setSettingError('Must be greater than 0');
      }
    } else if (mode === COPY_MODE) {
      setCopiesNumberError('');

      if (copiesNo === '' || isNaN(copiesNo)) {
        setCopiesNumberError('Field is not nullable');
      } else if (copiesNo < 0) {
        setCopiesNumberError('Must be greater than 0');
      }
    }
  };

  useEffect(validateFields);

  const getNextCellPatternClickHandler = () => {
    if (isNextCellPatternDisabled) return null;
    if (disabled) return null;

    return () => setAutoFillAndCopyPattern(AUTOFILL_PATTERN.NEXT_CELL);
  };

  const getNextPagePatternClickHandler = () => {
    if (disabled) return null;

    return () => setAutoFillAndCopyPattern(AUTOFILL_PATTERN.NEXT_PAGE);
  };

  const handleAutofillNumFieldChange = (type, value) => {
    if (!disabled) {
      onChange({ [type]: value });
    }
  };

  const handleAutofillNumFieldClick = (type, value) => {
    if (!disabled) {
      if (isNaN(value)) {
        handleAutofillNumFieldChange(type, '0');
      } else {
        handleAutofillNumFieldChange(type, value);
      }
    }
  };

  const handleNoneNegativeNumFieldClick = (type, value) => {
    if (!disabled) {
      onChange({ [type]: value });
    }
  };

  return (
    <FormWrapper>
      <InputContainerWhite>
        <InlineButtonsGroup backgroundColor="transparent" defaultBorderBottom="1px solid #e1e5e9 " items={modesList} />
      </InputContainerWhite>

      {isTextFlowAvailable && (
        <>
          <Label>
            <FormattedMessage id="app.editor.sideMenu.panelLabel.flow" defaultMessage="Flow" />
          </Label>

          <NextModeWrapper>
            <NextModeItem disabled={isNextCellPatternDisabled} onClick={getNextCellPatternClickHandler()}>
              <Icon src={pattern === AUTOFILL_PATTERN.NEXT_CELL ? IconRadioButtonSelected : IconRadioButton} />
              <Label>
                <FormattedMessage id="app.editor.sideMenu.panelLabel.nextCell" defaultMessage="Next cell" />
              </Label>
            </NextModeItem>

            <NextModeItem onClick={getNextPagePatternClickHandler()}>
              <Icon src={pattern === AUTOFILL_PATTERN.NEXT_PAGE ? IconRadioButtonSelected : IconRadioButton} />
              <Label>
                <FormattedMessage id="app.editor.sideMenu.panelLabel.nextPage" defaultMessage="Next page" />
              </Label>
            </NextModeItem>
          </NextModeWrapper>
        </>
      )}

      {mode === AUTOFILL_MODE && (
        <>
          <TakeOtherContent
            isMatrix={isMatrix}
            disabled={disabled}
            includeOtherContent={includeOtherContent}
            setOtherContentState={setOtherContentState}
          />

          <GridContainer gridTemplateColumns="1fr 1fr">
            <Label>
              <FormattedMessage id="app.editor.menu.autofill.start" defaultMessage="Start" />
            </Label>
            <Label>
              <FormattedMessage id="app.editor.menu.autofill.end" defaultMessage="End" />
            </Label>
            <InputContainerWhite>
              <AutofillNumericInput
                onMinusClick={handleAutofillNumFieldClick.bind(null, 'start', autofillStart - 1)}
                onInputChange={handleAutofillNumFieldChange.bind(null, 'start')}
                onPlusClick={handleAutofillNumFieldClick.bind(null, 'start', autofillStart + 1)}
                value={start}
                className="inputContainer"
                backgroundColor={colors.white}
                hoverColor={colors.whiteSmoke}
                selectedColor={colors.primary}
                iconMinus={IconMinus}
                iconPlus={IconPlus}
              />
            </InputContainerWhite>
            <InputContainerWhite>
              <AutofillNumericInput
                onMinusClick={handleAutofillNumFieldClick.bind(null, 'end', autofillEnd - 1)}
                onInputChange={handleAutofillNumFieldChange.bind(null, 'end')}
                onPlusClick={handleAutofillNumFieldClick.bind(null, 'end', autofillEnd + 1)}
                value={end}
                className="inputContainer"
                backgroundColor={colors.white}
                hoverColor={colors.whiteSmoke}
                selectedColor={colors.primary}
                iconMinus={IconMinus}
                iconPlus={IconPlus}
              />
            </InputContainerWhite>
          </GridContainer>

          <GridContainer gridTemplateColumns="1fr 1fr">
            <Label>
              <FormattedMessage id="app.editor.menu.autofill.steps" defaultMessage="Steps" />
            </Label>
            <div></div>
            <InputContainerWhite>
              <NoneNegativeNumericInput
                onMinusClick={handleNoneNegativeNumFieldClick.bind(null, 'steps', steps - 1)}
                onInputChange={handleNoneNegativeNumFieldClick.bind(null, 'steps')}
                onPlusClick={handleNoneNegativeNumFieldClick.bind(null, 'steps',  steps + 1)}
                value={steps}
                className="inputContainer"
                backgroundColor={colors.white}
                hoverColor={colors.whiteSmoke}
                selectedColor={colors.primary}
                iconMinus={IconMinus}
                iconPlus={IconPlus}
              />
            </InputContainerWhite>
            <div></div>
          </GridContainer>

          <GridContainer gridTemplateColumns="1fr 1fr">
            <Label>
              <FormattedMessage id="app.editor.menu.autofill.prefix" defaultMessage="Prefix" />
            </Label>
            <Label>
              <FormattedMessage id="app.editor.menu.autofill.suffix" defaultMessage="Suffix" />
            </Label>
            <Input value={prefix} onChange={e => (disabled ? undefined : onChange({ prefix: e.target.value }))} />
            <Input value={suffix} onChange={e => (disabled ? undefined : onChange({ suffix: e.target.value }))} />
          </GridContainer>

          <GridContainer key="autofill-extendedSettings-checkbox-grid" gridTemplateColumns="1fr 1fr">
            <PanelHeader>
              <FormattedMessage id="app.editor.sideMenu.panelLabel.extendedSettings" defaultMessage="Extended settings" />
            </PanelHeader>
            <div></div>
            <div className="inputContainerWhite" onClick={disabled ? undefined : () => onChange({ isSettingsVisible: !isSettingsVisible })}>
              <ToggleButton selected={isSettingsVisible}>
                <IconAdvancedSettings fill={isSettingsVisible ? colors.primary : colors.nero} />
              </ToggleButton>
            </div>
            <div></div>
          </GridContainer>

          {isSettingsVisible && (
            <>
              <GridContainer gridTemplateColumns="2fr 1fr">
                <NextModeWrapper>
                  <NextModeItem onClick={disabled ? undefined : () => onChange({ setting: AUTOFILL_SETTING_LOOP })}>
                    <Icon src={setting === AUTOFILL_SETTING_LOOP ? IconRadioButtonSelected : IconRadioButton} />
                    <Label>
                      <FormattedMessage id="app.editor.sideMenu.panelLabel.loop" defaultMessage="Loop" />
                    </Label>
                  </NextModeItem>
                  <NextModeItem onClick={disabled ? undefined : () => onChange({ setting: AUTOFILL_SETTING_REPEAT })}>
                    <Icon src={setting === AUTOFILL_SETTING_REPEAT ? IconRadioButtonSelected : IconRadioButton} />
                    <Label>
                      <FormattedMessage id="app.editor.sideMenu.panelLabel.repeat" defaultMessage="Repeat" />
                    </Label>
                  </NextModeItem>
                </NextModeWrapper>
                <div></div>
              </GridContainer>

              <GridContainer gridTemplateColumns="1fr 1fr">
                <Label>
                  <FormattedMessage id="app.editor.sideMenu.panelLabel.numberOfLoops" defaultMessage="Number of loops" />
                </Label>
                <div></div>
                {setting === AUTOFILL_SETTING_LOOP && (
                  <InputContainerWhite>
                    <NoneNegativeNumericInput
                      onMinusClick={handleNoneNegativeNumFieldClick.bind(null, 'loopsAmount', loopsAmount - 1)}
                      onInputChange={handleNoneNegativeNumFieldClick.bind(null, 'loopsAmount')}
                      onPlusClick={handleNoneNegativeNumFieldClick.bind(null, 'loopsAmount', loopsAmount + 1)}
                      value={loopsAmount}
                      className="inputContainer"
                      backgroundColor={colors.white}
                      hoverColor={colors.whiteSmoke}
                      selectedColor={colors.primary}
                      iconMinus={IconMinus}
                      iconPlus={IconPlus}
                    />
                  </InputContainerWhite>
                )}
                {setting === AUTOFILL_SETTING_REPEAT && (
                  <InputContainerWhite>
                    <NoneNegativeNumericInput
                      onMinusClick={handleNoneNegativeNumFieldClick.bind(null, 'repeatsAmount', repeatsAmount - 1)}
                      onInputChange={handleNoneNegativeNumFieldClick.bind(null, 'repeatsAmount')}
                      onPlusClick={handleNoneNegativeNumFieldClick.bind(null, 'repeatsAmount', repeatsAmount + 1)}
                      value={repeatsAmount}
                      className="inputContainer"
                      backgroundColor={colors.white}
                      hoverColor={colors.whiteSmoke}
                      selectedColor={colors.primary}
                      iconMinus={IconMinus}
                      iconPlus={IconPlus}
                    />
                  </InputContainerWhite>
                )}
              </GridContainer>
            </>
          )}
        </>
      )}

      {mode === COPY_MODE && (
        <>
          <TakeOtherContent
            isMatrix={isMatrix}
            disabled={disabled}
            includeOtherContent={includeOtherContent}
            setOtherContentState={setOtherContentState}
          />

          <GridContainer gridTemplateColumns="1fr 1fr">
            <Label>
              <FormattedMessage id="app.editor.menu.autofill.copiesNo" defaultMessage="Copies" />
            </Label>
            <div></div>
            <InputContainerWhite>
              <NoneNegativeNumericInput
                onMinusClick={handleNoneNegativeNumFieldClick.bind(null, 'copiesNo', copiesNo - 1)}
                onInputChange={handleNoneNegativeNumFieldClick.bind(null, 'copiesNo')}
                onPlusClick={handleNoneNegativeNumFieldClick.bind(null, 'copiesNo', copiesNo + 1)}
                value={copiesNo}
                className="inputContainer"
                backgroundColor={colors.white}
                hoverColor={colors.whiteSmoke}
                selectedColor={colors.primary}
                iconMinus={IconMinus}
                iconPlus={IconPlus}
              />
            </InputContainerWhite>
          </GridContainer>

          <div></div>
        </>
      )}
    </FormWrapper>
  );
};

export default AutofillAndCopyForm;
